'use client';

// base
import React, { useRef, useMemo } from 'react';

// // constants
// import { environmentIcon } from 'constants';

// libraries
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  Tooltip,
  Legend,
  type ChartOptions,
  type ScriptableContext,
  type Chart,
  type Plugin,
  PointElement
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend);

export interface LineChartDataSetProps {
  label: string;
  data: number[];
  borderColor: string;
  pointBackgroundColor: string;
  pointHoverBorderColor: string;
  gradientStart: string;
  gradientEnd: string;
  gradientStartPosition: number;
  gradientEndPosition: number;
  tension?: number;
}

interface LineChartProps {
  labels: string[];
  isWind?: boolean;
  labelColor?: string;
  xNoRotate?: boolean;
  yLabelStepSize?: number;
  customYTicks?: (value: string | number, index: number) => string;
  customXTicks?: (value: string | number, index: number) => string;
  customOptions?: ChartOptions<'line'>;
  dataSets: LineChartDataSetProps[];
}

export const LineChart: React.FC<LineChartProps> = ({
  labels,
  isWind = false,
  xNoRotate = false,
  labelColor = '#222222',
  yLabelStepSize = 5,
  dataSets,
  customOptions,
  customYTicks,
  customXTicks
}) => {
  const chartRef = useRef<ChartJS<'line'>>(null);

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            display: false,
            color: '#2222224D'
          },
          ticks: {
            color: labelColor,
            callback: (value: string | number, index: number) => {
              if (customXTicks) {
                return customXTicks(value, index);
              }

              return value;
            },
            minRotation: xNoRotate ? 0 : 45,
            maxRotation: xNoRotate ? 0 : 45
          }
        },
        y: {
          grid: {
            color: 'rgba(34, 34, 34, 0.3)',
            display: true,
            borderDash: [6, 4],
            drawOnChartArea: true,
            drawTicks: false,
            lineWidth: 0.5
          },
          ticks: {
            color: labelColor,
            stepSize: yLabelStepSize,
            callback: (value: string | number, index: number) => {
              if (customYTicks) {
                return customYTicks(value, index);
              }
              return value;
            }
          },
          border: {
            dash: [2, 4]
          },
          position: 'right',
          min: 0
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: true,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          borderColor: '#ffffff',
          borderWidth: 1
        }
      },
      ...customOptions
    }),
    [labelColor, customOptions, customYTicks, customXTicks, yLabelStepSize, xNoRotate]
  );

  const chartData = useMemo(
    () => ({
      labels: labels,
      datasets: dataSets.map((dataset) => ({
        label: dataset.label,
        data: dataset.data,
        backgroundColor: (context: ScriptableContext<'line'>) => {
          const ctx = context.chart.ctx;
          const chartArea = context.chart.chartArea;
          if (!chartArea) {
            return undefined;
          }
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);

          gradient.addColorStop(dataset.gradientStartPosition, dataset.gradientStart);
          gradient.addColorStop(dataset.gradientEndPosition, dataset.gradientEnd);

          return gradient;
        },
        borderColor: dataset.borderColor,
        borderWidth: 2,
        pointBackgroundColor: '#ffffff',
        pointBorderColor: dataset.borderColor,
        pointBorderWidth: 2,
        pointHoverBackgroundColor: dataset.borderColor,
        pointHoverBorderColor: dataset.borderColor,
        pointRadius: 3,
        pointHoverRadius: 5,
        fill: true,
        tension: dataset.tension ?? 0.4
      }))
    }),
    [labels, dataSets]
  );

  return <Line ref={chartRef} data={chartData} options={options} updateMode="resize" />;
};
